import { Spinner } from '@campx-org/shared'
import { Box, Typography } from '@mui/material'
import * as pdfjsLib from 'pdfjs-dist'
// @ts-ignore - CSS import for pdf.js viewer styling
import 'pdfjs-dist/web/pdf_viewer.css'
import { EvaluatorEditorStore } from 'components/EvaluationEditor/EvaluatorEditor'
import { useEffect, useRef } from 'react'
import { useImmer } from 'use-immer'

// Use local worker from node_modules to avoid CORS issues
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).href

export function PdfViewerSimple({ pdfUrl }: { pdfUrl: string }) {
  const { zoomLevel } = EvaluatorEditorStore.useState()

  if (!pdfUrl) {
    return (
      <Typography textAlign="center" color="text.secondary" sx={{ p: 4 }}>
        PDF not available.
      </Typography>
    )
  }

  const [state, setState] = useImmer({
    isLoading: true,
    isRenderingPages: false,
    loadingError: null as string | null,
  })

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const pdfRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)

  const renderPages = async () => {
    if (!pdfRef.current || state.isRenderingPages || !canvasContainerRef.current) return

    setState((s) => { s.isRenderingPages = true })

    const pdf = pdfRef.current
    if (canvasContainerRef.current) {
      canvasContainerRef.current.innerHTML = ''
    }

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum)
        const viewport = page.getViewport({ scale: zoomLevel })

        const canvas = document.createElement('canvas')
        const canvasContext = canvas.getContext('2d')
        if (!canvasContext) continue

        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.display = 'block'
        canvas.style.marginBottom = '12px'
        canvas.style.maxWidth = '100%'

        await page.render({ canvasContext, viewport }).promise

        if (canvasContainerRef.current) {
          canvasContainerRef.current.appendChild(canvas)
        }
      } catch {
        // skip page render errors
      }
    }

    setState((s) => {
      s.isRenderingPages = false
      s.isLoading = false
    })
  }

  useEffect(() => {
    const loadPDF = async () => {
      setState((s) => { s.isLoading = true; s.loadingError = null })
      if (canvasContainerRef.current) canvasContainerRef.current.innerHTML = ''
      try {
        pdfRef.current = await pdfjsLib.getDocument(pdfUrl).promise
        setState((s) => { s.isLoading = false })
      } catch (error) {
        setState((s) => { s.isLoading = false; s.loadingError = 'Failed to load PDF.' })
      }
    }
    loadPDF()
  }, [pdfUrl])

  useEffect(() => {
    if (pdfRef.current && !state.isLoading && canvasContainerRef.current) {
      renderPages()
    }
  }, [zoomLevel, state.isLoading])

  if (state.isLoading) return <Spinner />
  if (state.loadingError) return <Typography color="error">{state.loadingError}</Typography>

  return (
    <Box
      ref={canvasContainerRef}
      sx={{ p: 2, bgcolor: '#ffffff', minHeight: '100%' }}
    />
  )
}
